import { Card, CardContent } from "../../ui/card";
import { Button } from "../../ui/button";
import { Monitor, Apple, Laptop, Code, Server, ExternalLink } from "lucide-react";
import { SiLinkedin } from "react-icons/si";
import { useToast } from "../../../hooks/use-toast";
import { useEffect, useState } from "react";
import axios from "axios";
import { NGROK } from "../../../APIs";
import { FaApple, FaLinux, FaWindows } from "react-icons/fa6";

export default function AboutPage() {
    const { toast } = useToast();
    const [backendVersion, setBackendVersion] = useState<string>("N/A");
    const [windowsVersion, setWindowsVersion] = useState<string>("N/A");
    const [linuxVersion, setLinuxVersion] = useState<string>("N/A");
    const [macVersion, setMacVersion] = useState<string>("N/A");

    const fetchAgentVersion = async (os: "windows" | "linux" | "mac") => {
        try {
            const url = `${NGROK}/api/agent/${os}-agent-version`;
            const response = await axios.get(url);
            const version = response?.data ?? "N/A";
            switch (os) {
                case "windows": setWindowsVersion(version); break;
                case "linux": setLinuxVersion(version); break;
                case "mac": setMacVersion(version); break;
            }
        } catch (error) {
            switch (os) {
                case "windows": setWindowsVersion("N/A"); break;
                case "linux": setLinuxVersion("N/A"); break;
                case "mac": setMacVersion("N/A"); break;
            }
            console.error(`Failed to fetch ${os} agent version`, error);
        }
    };

    const fetchBackendVersion = async () => {
        try {
            const response = await axios.get(`${NGROK}/api/build-version`);
            setBackendVersion(response?.data ?? "N/A");
        } catch (error) {
            setBackendVersion("N/A");
            console.error("Failed to fetch backend version", error);
        }
    };

    useEffect(() => {
        fetchBackendVersion();
        fetchAgentVersion("windows");
        fetchAgentVersion("linux");
        fetchAgentVersion("mac");
    }, []);

    const handleLinkClick = (linkName: string, url?: string) => {
        if (url) window.open(url, "_blank", "noopener,noreferrer");

        toast({
            title: "Link Opened",
            description: `Opening ${linkName}...`,
        });
    };

    const platformVersions = [
        { id: "frontend", label: "Frontend Version", version: "5.3.117", icon: Code, description: "React-based web interface" },
        { id: "backend", label: "Backend Version", version: backendVersion, icon: Server, description: "Node.js API service" }
    ];

    const agentVersions = [
        { id: "windows", label: "Windows Version", version: windowsVersion, icon: FaWindows, description: "Windows agent service", color: "text-blue-600" },
        { id: "linux", label: "Linux Version", version: linuxVersion, icon: FaLinux, description: "Linux daemon service", color: "text-black dark:text-white" },
        { id: "mac", label: "Mac Version", version: macVersion, icon: FaApple, description: "macOS agent service", color: "text-gray-700 dark:text-gray-300" }
    ];

    const companyLinks = [
        { id: "linkedin", label: "LinkedIn", icon: SiLinkedin, url: "https://www.linkedin.com/company/whiteswan-identity-security/", color: "text-blue-600 hover:text-blue-700" },
        { id: "about", label: "About Us", icon: ExternalLink, url: "https://www.whiteswansecurity.com/about/", color: "text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300" },
        { id: "contact", label: "Contact Us", icon: ExternalLink, url: "https://www.whiteswansecurity.com/contact/", color: "text-slate-600 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-300" },
        { id: "whiteswan", label: "WhiteSwan", icon: ExternalLink, url: "https://www.whiteswansecurity.com/", color: "text-slate-800 hover:text-slate-900 dark:text-slate-200 dark:hover:text-slate-100" }
    ];

    return (
        <div className="min-h-full bg-background">
            {/* Header with Branding */}
            <div className="py-8 px-6">
                <div className="bg-slate-800 dark:bg-slate-900 py-8 px-6">
                    <div className="max-w-4xl mx-auto text-center">
                        <div className="flex items-center justify-center gap-3 mb-4">
                            <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center">
                                <div className="w-8 h-8 bg-blue-600 rounded-md flex items-center justify-center">
                                    <span className="text-white font-bold text-sm">WS</span>
                                </div>
                            </div>
                            <div className="text-white">
                                <h1 className="text-2xl font-bold">WHITESWAN</h1>
                                <p className="text-sm text-slate-300 uppercase tracking-wider">IDENTITY SECURITY</p>
                            </div>
                        </div>
                        <p className="text-slate-300 max-w-2xl mx-auto">
                            Platform and agent version information for your WhiteSwanSecurity deployment
                        </p>
                    </div>
                </div>
            </div>

            {/* Version Information */}
            <div className="max-w-4xl mx-auto px-6 py-12">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                    {/* Platform Versions */}
                    <div>
                        <h2 className="text-2xl font-semibold mb-8 text-center">Platform</h2>
                        <div className="space-y-6">
                            {platformVersions.map(platform => (
                                <Card key={platform.id} className="group hover-elevate transition-all duration-200">
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-4">
                                            <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                                                <platform.icon className="w-6 h-6 text-blue-600" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-lg">{platform.label}</h3>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className="text-xl font-mono font-bold">{platform.version || "N/A"}</div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>

                    {/* Agent Versions */}
                    <div>
                        <h2 className="text-2xl font-semibold mb-8 text-center">Agent</h2>
                        <div className="space-y-6">
                            {agentVersions.map(agent => (
                                <Card key={agent.id} className="group hover-elevate transition-all duration-200">
                                    <CardContent className="p-6">
                                        <div className="flex items-center gap-4">
                                            <div className={`w-12 h-12 bg-gray-100 dark:bg-gray-900/20 rounded-lg flex items-center justify-center`}>
                                                <agent.icon className={`w-6 h-6 ${agent.color}`} />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <h3 className="font-semibold text-lg">{agent.label}</h3>
                                                <p className="text-sm text-muted-foreground">{agent.description}</p>
                                            </div>
                                            <div className="text-right flex-shrink-0">
                                                <div className="text-xl font-mono font-bold">{agent.version || "N/A"}</div>
                                            </div>
                                        </div>
                                    </CardContent>
                                </Card>
                            ))}
                        </div>
                    </div>
                </div>

                {/* Company Links */}
                <div className="mt-16 pt-12 border-t">
                    <div className="flex flex-wrap items-center justify-center gap-8">
                        {companyLinks.map(link => (
                            <Button
                                key={link.id}
                                variant="ghost"
                                size="lg"
                                onClick={() => handleLinkClick(link.label, link.url)}
                                className={`gap-3 ${link.color} hover:bg-muted/50`}
                                data-testid={`link-${link.id}`}
                            >
                                <link.icon className="w-5 h-5" />
                                {link.label}
                            </Button>
                        ))}
                    </div>
                </div>

                {/* Footer */}
                <div className="mt-16 pt-8 border-t text-center">
                    <p className="text-sm text-muted-foreground">
                        © 2026 Whiteswan Security Inc. All rights reserved.
                    </p>
                </div>
            </div>
        </div>
    );
}
